import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useOffices, useDeleteOffice } from "@/lib/api/offices/hooks";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit } from "lucide-react";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { AdminGuard } from "@/components/shared/admin-guard";
import { toast } from "sonner";
import { OfficeDialog } from "@/components/offices/office-dialog";
import { Office } from "@/lib/api/offices/types";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";

function OfficesPage() {
  const [editingOffice, setEditingOffice] = useState<Office | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const { data: offices, isLoading } = useOffices();
  const deleteOffice = useDeleteOffice();

  const handleCreate = () => {
    setEditingOffice(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (office: Office) => {
    setEditingOffice(office);
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string, name: string) => {
    setAlertDialogState({
      title: "지점 삭제",
      description: `정말 "${name}" 지점을 삭제하시겠습니까?`,
      confirmText: "삭제",
      onConfirm: async () => {
        resetAlertDialogState();
        try {
          await deleteOffice.mutateAsync(id);
          toast.success("삭제되었습니다.");
        } catch {
          toast.error("삭제에 실패했습니다.");
        }
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  };

  useLoadingIndicator(isLoading);

  return (
    <AdminGuard>
      <PageContainer>
        <Header
          title="지점 관리"
          right={
            <Button onClick={handleCreate}>
              <Plus className="size-4 mr-2" />새 지점
            </Button>
          }
        />

      {/* 지점 목록 */}
      <div className="bg-card rounded-lg border">
        {offices && offices.length > 0 ? (
          <>
            {/* 테이블 헤더 */}
            <div className="hidden md:grid grid-cols-5 gap-4 p-4 border-b font-semibold text-sm text-muted-foreground">
              <div>지점명</div>
              <div>주소</div>
              <div>전화번호</div>
              <div>상태</div>
              <div></div>
            </div>

            {/* 테이블 바디 */}
            <div className="divide-y">
              {offices.map((office) => (
                <div
                  key={office._id}
                  className="p-4 hover:bg-muted/50 transition-colors"
                >
                  {/* 데스크톱 뷰 */}
                  <div className="hidden md:grid grid-cols-5 gap-4 items-center">
                    <div className="font-medium">{office.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {office.address || "-"}
                    </div>
                    <div className="text-sm">{office.phoneNumber || "-"}</div>
                    <div>
                      <span
                        className={`inline-flex px-2 py-1 text-xs rounded ${
                          office.status === "active"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {office.status === "active" ? "활성" : "비활성"}
                      </span>
                    </div>
                    <div className="flex gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(office)}
                      >
                        <Edit className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(office._id, office.name)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </div>

                  {/* 모바일 뷰 */}
                  <div className="md:hidden space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{office.name}</span>
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs rounded ${
                            office.status === "active"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {office.status === "active" ? "활성" : "비활성"}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(office)}
                        >
                          <Edit className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(office._id, office.name)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </div>
                    {(office.address || office.phoneNumber) && (
                      <div className="text-sm text-muted-foreground">
                        {office.address}
                        {office.address && office.phoneNumber && " · "}
                        {office.phoneNumber}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            등록된 지점이 없습니다.
          </div>
        )}
      </div>

        {/* 생성/수정 다이얼로그 */}
        <OfficeDialog
          office={editingOffice}
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
        />
      </PageContainer>
    </AdminGuard>
  );
}

export const Route = createFileRoute("/_content/offices")({
  component: OfficesPage,
});
